import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";

import Login from "@/pages/client/Login";
import Signup from "@/pages/client/Signup";
import AuthCallback from "@/pages/client/AuthCallback";
import Onboarding from "@/pages/client/Onboarding";
import Dashboard from "@/pages/client/Dashboard";
import FundExplorer from "@/pages/client/FundExplorer";
import FundDetail from "@/pages/client/FundDetail";
import Invest from "@/pages/client/Invest";
import Redeem from "@/pages/client/Redeem";
import SwitchOrder from "@/pages/client/SwitchOrder";
import SipCenter from "@/pages/client/SipCenter";
import SipNew from "@/pages/client/SipNew";
import Mandates from "@/pages/client/Mandates";
import Orders from "@/pages/client/Orders";
import Payments from "@/pages/client/Payments";
import Statements from "@/pages/client/Statements";
import Profile from "@/pages/client/Profile";
import Portfolio from "@/pages/client/Portfolio";
import Goals from "@/pages/client/Goals";
import Watchlist from "@/pages/client/Watchlist";
import Compare from "@/pages/client/Compare";
import Tools from "@/pages/client/Tools";
import Family from "@/pages/client/Family";
import Alternates from "@/pages/client/Alternates";
import Documents from "@/pages/client/Documents";
import AlternatesAdmin from "@/pages/admin/AlternatesAdmin";
import Servicing from "@/pages/admin/Servicing";
import Research from "@/pages/Research";
import AifProviderPortal from "@/pages/AifProviderPortal";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ClientQueue from "@/pages/admin/ClientQueue";
import ClientDetail from "@/pages/admin/ClientDetail";
import SchemeMaster from "@/pages/admin/SchemeMaster";
import OrdersMonitor from "@/pages/admin/OrdersMonitor";
import SipsMonitor from "@/pages/admin/SipsMonitor";
import MandatesMonitor from "@/pages/admin/MandatesMonitor";
import PaymentsMonitor from "@/pages/admin/PaymentsMonitor";
import RtaFeeds from "@/pages/admin/RtaFeeds";
import AdvisorDesk from "@/pages/admin/AdvisorDesk";
import Revenue from "@/pages/admin/Revenue";
import UserManagement from "@/pages/admin/UserManagement";
import Bse2Console from "@/pages/admin/Bse2Console";
import UccOnboarding from "@/pages/admin/UccRegistrationV203";
import UccServicing from "@/pages/admin/UccServicing";
import BseCredentials from "@/pages/admin/BseCredentials";
import ExchangeReconciliation from "@/pages/admin/ExchangeReconciliation";
import ResearchOperations from "@/pages/admin/ResearchOperations";
import OperationsGuide from "@/pages/admin/OperationsGuide";
import BseUatDossier from "@/pages/admin/BseUatDossier";
import BseApiWorkbench from "@/pages/admin/BseApiWorkbench";

function AppRoutes() {
  const location = useLocation();
  // Google OAuth returns with #session_id= — process it before any route/auth guard runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
              {/* Client auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/research" element={<Research />} />
              <Route path="/aif-provider" element={<AifProviderPortal />} />

              {/* Client app */}
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
              <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
              <Route path="/watchlist" element={<ProtectedRoute><Watchlist /></ProtectedRoute>} />
              <Route path="/compare" element={<ProtectedRoute><Compare /></ProtectedRoute>} />
              <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
              <Route path="/family" element={<ProtectedRoute><Family /></ProtectedRoute>} />
              <Route path="/alternates" element={<ProtectedRoute><Alternates /></ProtectedRoute>} />
              <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/funds" element={<ProtectedRoute><FundExplorer /></ProtectedRoute>} />
              <Route path="/funds/:schemeCode" element={<ProtectedRoute><FundDetail /></ProtectedRoute>} />
              <Route path="/invest/:schemeCode" element={<ProtectedRoute><Invest /></ProtectedRoute>} />
              <Route path="/redeem" element={<ProtectedRoute><Redeem /></ProtectedRoute>} />
              <Route path="/switch" element={<ProtectedRoute><SwitchOrder /></ProtectedRoute>} />
              <Route path="/sips" element={<ProtectedRoute><SipCenter /></ProtectedRoute>} />
              <Route path="/sips/new" element={<ProtectedRoute><SipNew /></ProtectedRoute>} />
              <Route path="/mandates" element={<ProtectedRoute><Mandates /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
              <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
              <Route path="/statements" element={<ProtectedRoute><Statements /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

              {/* Admin auth */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Admin app */}
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/clients" element={<AdminProtectedRoute><ClientQueue /></AdminProtectedRoute>} />
              <Route path="/admin/users" element={<AdminProtectedRoute><UserManagement /></AdminProtectedRoute>} />
              <Route path="/admin/bse2" element={<AdminProtectedRoute><Bse2Console /></AdminProtectedRoute>} />
              <Route path="/admin/bse2/uat-dossier" element={<AdminProtectedRoute><BseUatDossier /></AdminProtectedRoute>} />
              <Route path="/admin/bse2/workbench" element={<AdminProtectedRoute><BseApiWorkbench /></AdminProtectedRoute>} />
              <Route path="/admin/ucc-onboarding" element={<AdminProtectedRoute><UccOnboarding /></AdminProtectedRoute>} />
              <Route path="/admin/ucc-servicing" element={<AdminProtectedRoute><UccServicing /></AdminProtectedRoute>} />
              <Route path="/admin/bse-credentials" element={<AdminProtectedRoute><BseCredentials /></AdminProtectedRoute>} />
              <Route path="/admin/bse-reconciliation" element={<AdminProtectedRoute><ExchangeReconciliation /></AdminProtectedRoute>} />
              <Route path="/admin/clients/:userId" element={<AdminProtectedRoute><ClientDetail /></AdminProtectedRoute>} />
              <Route path="/admin/schemes" element={<AdminProtectedRoute><SchemeMaster /></AdminProtectedRoute>} />
              <Route path="/admin/orders" element={<AdminProtectedRoute><OrdersMonitor /></AdminProtectedRoute>} />
              <Route path="/admin/redemptions" element={<AdminProtectedRoute><OrdersMonitor initialKind="redemption" /></AdminProtectedRoute>} />
              <Route path="/admin/switches" element={<AdminProtectedRoute><OrdersMonitor initialKind="switch" /></AdminProtectedRoute>} />
              <Route path="/admin/sips" element={<AdminProtectedRoute><SipsMonitor /></AdminProtectedRoute>} />
              <Route path="/admin/mandates" element={<AdminProtectedRoute><MandatesMonitor /></AdminProtectedRoute>} />
              <Route path="/admin/payments" element={<AdminProtectedRoute><PaymentsMonitor /></AdminProtectedRoute>} />
              <Route path="/admin/rta" element={<AdminProtectedRoute><RtaFeeds /></AdminProtectedRoute>} />
              <Route path="/admin/research-ops" element={<AdminProtectedRoute><ResearchOperations /></AdminProtectedRoute>} />
              <Route path="/admin/operations-guide" element={<AdminProtectedRoute><OperationsGuide /></AdminProtectedRoute>} />
              <Route path="/admin/crm" element={<AdminProtectedRoute><AdvisorDesk /></AdminProtectedRoute>} />
              <Route path="/admin/revenue" element={<AdminProtectedRoute><Revenue /></AdminProtectedRoute>} />
              <Route path="/admin/alternates" element={<AdminProtectedRoute><AlternatesAdmin /></AdminProtectedRoute>} />
              <Route path="/admin/servicing" element={<AdminProtectedRoute><Servicing /></AdminProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AdminAuthProvider>
            <Toaster richColors position="bottom-right" />
            <AppRoutes />
          </AdminAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
